import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { UserService } from 'bookraw/shared/services/user.service';
import { environment } from 'env/environment';
import { catchError, finalize, shareReplay } from 'rxjs/operators';
import { UserType } from 'bookraw/shared/models/user.model';

interface CacheStorage {
  [key: string]: {
    time: number,
    observable: Observable<any>
  };
}

/**
 * Összevonja a párhuzamosan futó requesteket
 */
export class RequestCache {
  private cache: CacheStorage = {};

  deleteCache(key: string) {
    delete this.cache[key];
  }

  wrap<TModel>(request: HttpRequest<TModel>, handler: HttpHandler): Observable<HttpEvent<TModel>> {
    if (request.method === 'GET') {
      const key = request.urlWithParams;
      const cacheEntry = this.cache[key];
      // csak 5 secen belül nem indítunk új requestet
      if (cacheEntry && cacheEntry.time > (Date.now() - 1000 * 5))
        return this.cache[key].observable;

      // töröljük a cachet mert lejárt
      if (cacheEntry)
        delete this.cache[key];

      this.cache[key] = {
        time: Date.now(),
        observable: handler.handle(request).pipe(
          catchError((err: any) => {
            if ( err instanceof HttpErrorResponse && err.status === 503 ) {
              window.location.href = '/maintenance';
            } else {
              throw err;
            }
            return of(err);
          }),
          finalize(() => this.deleteCache(key)),
          shareReplay()
        )
      };
      return this.cache[key].observable;
    }

    return handler.handle(request);
  }
}

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
  private cache = new RequestCache();

  constructor(
    private userService: UserService
  ) {}

  private isApiRequest<TResponse>(request: HttpRequest<TResponse>) {
    return request.url.indexOf(environment.baseURL) !== -1;
  }

  intercept<TResponse>(request: HttpRequest<TResponse>, next: HttpHandler): Observable<HttpEvent<TResponse>> {
    // Amennyiben fájlt (blobot) várunk az API-tól, akkor az Angular a hibaüzeneteket is ennek megfelelően kezeli,
    // viszont a szervertől ebbem az esetbem is a JSON választ tudjuk jól kezelni
    const blobErrorHandler = (err: HttpErrorResponse) => {
      if (request.responseType === 'blob' && err.error instanceof Blob) {
        return from(Promise.resolve(err).then(async x => {
          throw new HttpErrorResponse({
            error: JSON.parse(await x.error.text()),
            headers: x.headers,
            status: x.status,
            statusText: x.statusText,
            url: x.url ?? undefined
          })
        }));
      }
      return throwError(err);
    };

    // ha van token akkor berakjuk headerbe és küldjük minden requesthez
    if (this.isApiRequest(request) && this.userService.token) {
      const countryCode = this.userService.countryCode || null;
      const selectedSchoolId = this.userService.school?.id || null;
      const selectedPartnerId = this.userService.partner?.id || null;
      const clonedRequest = request.clone({
        params: (this.userService.user.type === UserType.ADULT && selectedSchoolId !== null ?
          request.params.set('locale', environment.locale).set('schoolId', selectedSchoolId.toString()) : (
            this.userService.user.type === UserType.ADMIN && selectedPartnerId !== null ?
            request.params.set('locale', environment.locale).set('globalPartnerId', selectedPartnerId.toString()) :
            request.params.set('locale', environment.locale)
          ))
          .set('countryCode', countryCode)
          .set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone),
        headers: request.headers
          .set('Authorization', `${this.userService.tokenType} ${this.userService.token}`)
      });
      return next.handle(clonedRequest).pipe(catchError(blobErrorHandler))
      // FIXME: Ez a cache megtöri a lekérdezések megszakításának lehetőségét. Vagy meg kell javítani, vagy törölni
      //  kell, mert jelenleg nagyobb problémát okoz, mint amit megold. Egyébként sem vagyok benne biztos, hogy jól
      //  működik...
      // return this.cache.wrap(clonedRequest, next);
    }

    return next.handle(request).pipe(catchError(blobErrorHandler))
    // FIXME: Ez a cache megtöri a lekérdezések megszakításának lehetőségét. Vagy meg kell javítani, vagy törölni
    //  kell, mert jelenleg nagyobb problémát okoz, mint amit megold. Egyébként sem vagyok benne biztos, hogy jól
    //  működik...
    // return this.cache.wrap(request, next);
  }
}
